import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiError, API_BASE, apiFetch } from "@/lib/api";
import { Translate, UploadSimple, Trash, CheckCircle, DownloadSimple } from "@phosphor-icons/react";
import { I18N_KEYS } from "@/lib/i18nCatalog";
import FrenchStringsEditor from "@/components/admin/FrenchStringsEditor";
import EmailTextsEditor from "@/components/admin/EmailTextsEditor";

const COMMON = [
  { code: "fr", label: "French (Français)" },
  { code: "es", label: "Spanish (Español)" },
  { code: "zh", label: "Mandarin Chinese (中文)" },
  { code: "pa", label: "Punjabi (ਪੰਜਾਬੀ)" },
  { code: "tl", label: "Tagalog" },
  { code: "ar", label: "Arabic (العربية)" },
];

export default function TranslationsPanel({ contentOnly = false }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [lang, setLang] = useState("fr");
  const [customLang, setCustomLang] = useState("");
  const [label, setLabel] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [okMsg, setOkMsg] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true); setErr("");
    try { const { data } = await api.get("/admin/translations"); setList(data); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const effectiveLang = lang === "__custom__" ? customLang.trim().toLowerCase() : lang;

  const upload = async () => {
    setErr(""); setOkMsg("");
    if (!effectiveLang) { setErr("Choose or type a language code"); return; }
    if (!file) { setErr("Choose a .json file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("lang", effectiveLang);
      if (label) fd.append("label", label);
      fd.append("file", file);
      const res = await apiFetch(`${API_BASE}/admin/translations`, { method: "POST", body: fd });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || "Upload failed"); }
      const d = await res.json();
      setOkMsg(`Uploaded ${d.lang.toUpperCase()} — ${d.key_count} keys.`);
      setFile(null); setLabel(""); setCustomLang(""); if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete the ${l.toUpperCase()} translation file?`)) return;
    try { await api.delete(`/admin/translations/${l}`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div>
    {!contentOnly && (
    <div className="card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-xl font-bold font-display flex items-center gap-2"><Translate size={22} weight="duotone" color="#CCFF00" /> Translations ({list.length})</h2>
        <button data-testid="download-template-btn" className="btn-ghost text-sm flex items-center gap-1.5 border border-[#C4C8CE] rounded-xl"
          title="JSON of every translatable string — send it to a translator, then upload the result"
          onClick={() => {
            const obj = Object.fromEntries(I18N_KEYS.map((k) => [k, k]));
            const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob); a.download = "strings-template-en.json"; a.click();
          }}>
          <DownloadSimple size={16} weight="bold" /> Download English template
        </button>
      </div>
      <p className="text-sm text-[#000000] mb-4">
        Upload a translated strings file (JSON) received from a translator. English ships by default; every uploaded language becomes selectable in the app's language switcher. No code changes needed.
      </p>

      {err && <div className="text-[#FF5100] mb-3 text-sm" data-testid="translations-error">{err}</div>}
      {okMsg && <div className="text-[#1a7f37] mb-3 text-sm flex items-center gap-1" data-testid="translations-ok"><CheckCircle size={14} weight="fill" /> {okMsg}</div>}

      <div className="p-4 rounded-2xl bg-[#DCDEE2] border border-[#C4C8CE] flex flex-col gap-3 mb-5">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-xs uppercase tracking-[0.12em] text-[#000000]">
            Language
            <select data-testid="translation-lang-select" className="input-base" value={lang} onChange={(e) => setLang(e.target.value)}>
              {COMMON.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
              <option value="__custom__">Other (type code)…</option>
            </select>
          </label>
          {lang === "__custom__" ? (
            <label className="flex flex-col gap-1 text-xs uppercase tracking-[0.12em] text-[#000000]">
              Custom code (e.g. de)
              <input data-testid="translation-custom-lang" className="input-base" placeholder="ISO code" value={customLang} onChange={(e) => setCustomLang(e.target.value)} />
            </label>
          ) : (
            <label className="flex flex-col gap-1 text-xs uppercase tracking-[0.12em] text-[#000000]">
              Display label (optional)
              <input data-testid="translation-label" className="input-base" placeholder="e.g. Français" value={label} onChange={(e) => setLabel(e.target.value)} />
            </label>
          )}
        </div>
        <input ref={fileRef} type="file" accept=".json,application/json" data-testid="translation-file-input"
          className="text-sm text-[#000000] file:bg-[#CCFF00] file:text-[#000000] file:font-bold file:rounded-full file:px-4 file:py-2 file:border-0 file:mr-3 file:cursor-pointer"
          onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <button className="btn-primary self-start flex items-center gap-1.5" onClick={upload} disabled={busy} data-testid="upload-translation-button">
          <UploadSimple size={16} weight="bold" /> {busy ? "Uploading…" : "Upload translation"}
        </button>
      </div>

      {loading ? <div className="text-[#000000] text-sm">Loading…</div> : (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#FFFFFF] border border-[#C4C8CE]" data-testid="translation-row-en">
            <span className="chip text-xs font-bold" style={{ background: "rgba(204,255,0,0.14)", color: "#7a8a00", border: "1px solid rgba(204,255,0,0.35)" }}>EN</span>
            <div className="flex-1 text-sm font-semibold">English <span className="text-[#000000] font-normal">· default (built-in)</span></div>
          </div>
          {list.map(t => (
            <motion.div key={t.lang} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#FFFFFF] border border-[#C4C8CE]" data-testid={`translation-row-${t.lang}`}>
              <span className="chip text-xs font-bold" style={{ background: "rgba(0,184,212,0.14)", color: "#00768a", border: "1px solid rgba(0,184,212,0.35)" }}>{t.lang.toUpperCase()}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{t.label || t.lang}</div>
                <div className="text-xs text-[#000000]">{t.key_count} keys · updated {new Date(t.updated_at).toLocaleDateString()}</div>
              </div>
              <a href={`${API_BASE}/admin/translations/${t.lang}/data`} target="_blank" rel="noopener noreferrer" className="btn-ghost p-1.5 text-[#000000]" title="View JSON"><DownloadSimple size={16} /></a>
              <button className="btn-ghost p-1.5" onClick={() => remove(t.lang)} data-testid={`delete-translation-${t.lang}`} title="Delete"><Trash size={16} color="#FF5100" /></button>
            </motion.div>
          ))}
          {list.length === 0 && <div className="text-[#000000] text-sm italic px-1">No uploaded languages yet. English is always available.</div>}
        </div>
      )}
    </div>
    )}
    <FrenchStringsEditor />
    <EmailTextsEditor />
    </div>
  );
}
